/* import __COLOCATED_TEMPLATE__ from './channel-options.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { IntegrationState } from 'embercom/objects/standalone/constants';
import type AppService from 'embercom/services/app-service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { type ComponentLike } from '@glint/template';
import { type BotConfigTarget } from 'embercom/objects/operator/configuration/configuration';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

interface Signature {
  Args: Args;
  Element: HTMLElement;
}

interface Args {
  channels: Array<Channel>;
}

export interface Channel {
  icon: InterfaceIconName;
  title: string;
  externalTypeName: string;
  integrationState: IntegrationState;
  setupArgs: ChannelSetupArgs;
  summaryComponent?: ComponentLike; // By default, we will render the ChannelSummary component unless specified
  summaryArgs?: ChannelSummaryArgs;
  disabled?: boolean;
  disabledTooltip?: string;
}

export interface ReactRouteArgs {
  route: string;
  urlSegments: {
    platform: string;
    channel: string;
  };
  routeParams: Record<string, string>;
}

export interface ChannelSetupArgs {
  description: string;
  route?: string;
  reactRoute?: ReactRouteArgs;
  imageAssetUrl: string;
  recommendedTooltip?: string;
  sectionTitle: string | undefined;
  articleId?: number;
  articleUrl?: string;
}
export interface ChannelSummaryArgs {
  workflowRoute: string;
  manageRoute?: string;
  manageRouteReact?: ReactRouteArgs;
  statusPill?: ComponentLike;
  workflowParams: {
    target: BotConfigTarget;
    objectType: number;
  };
  allowNoWorkflows?: boolean;
}

type GroupedSetupOption = {
  sectionTitle: string | undefined;
  options: Channel[];
};

export default class StandaloneDeployOverviewChannelOptions extends Component<Signature> {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;

  get activatedChannels(): Channel[] {
    return this.args.channels
      .filter((channel) => !this.setupChannels.includes(channel))
      .sort((a, b) => {
        let aIsEnabled = a.integrationState === IntegrationState.Enabled ? 0 : 1;
        let bIsEnabled = b.integrationState === IntegrationState.Enabled ? 0 : 1;
        return aIsEnabled - bIsEnabled;
      });
  }

  get app() {
    return this.appService.app;
  }

  get setupChannels(): Channel[] {
    let setupChannels = this.args.channels;

    if (
      !this.finStandaloneService.hasStandalonePlatform('salesforce') &&
      !this.finStandaloneService.platform.canUseReactStepBasedIA
    ) {
      setupChannels = setupChannels.filter((channel) => {
        {
          return [IntegrationState.NotConfigured, IntegrationState.Created].includes(
            channel.integrationState,
          );
        }
      });
    }

    return setupChannels.sort((a, b) => {
      let aIsEnabled = a.integrationState === IntegrationState.Enabled ? 0 : 1;
      let bIsEnabled = b.integrationState === IntegrationState.Enabled ? 0 : 1;
      return aIsEnabled - bIsEnabled;
    });
  }

  get groupedSetupOptions(): GroupedSetupOption[] {
    let groupedSetupOptions: GroupedSetupOption[] = [];

    this.setupChannels.forEach((setupChannel) => {
      let section = groupedSetupOptions.find(
        (section) => section.sectionTitle === setupChannel.setupArgs.sectionTitle,
      );
      if (section) {
        section.options.push(setupChannel);
      } else {
        groupedSetupOptions.push({
          sectionTitle: setupChannel.setupArgs.sectionTitle,
          options: [setupChannel],
        });
      }
    });

    return groupedSetupOptions;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Overview::ChannelOptions': typeof StandaloneDeployOverviewChannelOptions;
    'standalone/deploy/overview/channel-options': typeof StandaloneDeployOverviewChannelOptions;
  }
}
